import { useCallback, useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import * as ToastPrimitive from '@radix-ui/react-toast'

import { ToastState } from './ToastState'
import type {
  ToastAction,
  ToastEvent,
  ToastId,
  ToasterToast,
} from './ToastState'

import { Toast } from './Toast'
import { VisuallyHidden } from '../VisuallyHidden'

import styles from './Toaster.module.css'
import toastStyles from './Toast.module.css'

type InternalToast = ToasterToast & {
  dismissed?: boolean
}

function Toaster() {
  const [toasts, setToasts] = useState<InternalToast[]>([])

  const dismissToast = useCallback((id: ToastId) => {
    setToasts((toasts) =>
      toasts.map((toast) =>
        toast.id === id ? { ...toast, dismissed: true } : toast,
      ),
    )
  }, [])

  const removeToast = useCallback((id: ToastId) => {
    setToasts((toasts) => toasts.filter((toast) => toast.id !== id))
    ToastState.remove(id)
  }, [])

  useEffect(() => {
    const dismissedIds = toasts.filter((t) => t.dismissed).map((t) => t.id)
    if (dismissedIds.length > 0) {
      const timer = setTimeout(() => {
        dismissedIds.forEach(removeToast)
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [toasts, removeToast])

  useEffect(() => {
    function subscriber(event: ToastEvent) {
      if (event.type === 'dismiss') {
        dismissToast(event.id)
        return
      }

      if (event.type === 'dismissAll') {
        setToasts((toasts) =>
          toasts.map((toast) => ({ ...toast, dismissed: true })),
        )
        return
      }

      if (event.type === 'remove') {
        return
      }

      // push event
      const toast = event.toast

      setToasts((existingToasts) => {
        const existingToastIndex = existingToasts.findIndex(
          (existingToast) => existingToast.id === toast.id,
        )

        // update the toast if it already exists
        if (existingToastIndex > -1) {
          return existingToasts.map((item, index) =>
            index !== existingToastIndex ? item : { ...item, ...toast },
          )
        }

        // add the toast if it doesn't exist
        return [toast, ...existingToasts]
      })
    }

    return ToastState.subscribe(subscriber)
  }, [dismissToast, removeToast])

  return (
    <ToastPrimitive.Provider>
      {toasts.map((toast) => {
        const allActions: ToastAction[] = [
          ...(toast.action ? [toast.action] : []),
          ...(toast.actions ?? []),
        ]

        return (
          <Toast
            key={toast.id}
            variant={toast.variant}
            description={toast.description}
            open={!toast.dismissed}
            duration={toast.duration}
            dismissible={toast.dismissible}
            onDismiss={() => {
              dismissToast(toast.id)
              toast.onDismiss?.(toast.id)
            }}
          >
            {toast.message}
            {allActions.length > 0 && (
              <Toast.Actions>
                {allActions.map((action, index) => (
                  <button
                    key={index}
                    type="button"
                    className={toastStyles.actionLink}
                    onClick={action.onClick}
                  >
                    {action.label}
                    {action.altText && (
                      <VisuallyHidden>{action.altText}</VisuallyHidden>
                    )}
                  </button>
                ))}
              </Toast.Actions>
            )}
          </Toast>
        )
      })}
      <ToastViewportInBody />
    </ToastPrimitive.Provider>
  )
}

/**
 * A stacking context above the viewport (`isolation`, `transform`, `opacity`)
 * would confine its z-index to that subtree, letting anything portalled to the
 * body — every dialog — paint over it. Portalling puts both in one context.
 */
function ToastViewportInBody(): JSX.Element | null {
  const [container, setContainer] = useState<HTMLElement | null>(null)

  // Set after mount so server rendering has nothing to portal into.
  useEffect(() => {
    setContainer(document.body)
  }, [])

  if (!container) {
    return null
  }

  return createPortal(
    <ToastPrimitive.Viewport className={styles.viewport} />,
    container,
  )
}

Toaster.displayName = 'Toaster'

export { Toaster }
